import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, Renderer2, ViewChild } from '@angular/core';
import { SignaturePadConfig } from '../models/signature-pad';
import SignaturePad, { PointGroup } from 'signature_pad';

@Component({
  selector: 'signature-canvas',
  templateUrl: './signature-canvas.component.html',
  styleUrls: ['./signature-canvas.component.scss']
})
export class SignatureCanvasComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvasRef', { static: true }) canvasRef: ElementRef;

  @Input() public options: SignaturePadConfig;

  @Output() public onBeginEvent: EventEmitter<boolean>;
  @Output() public onEndEvent: EventEmitter<boolean>;

  private signaturePad: SignaturePad;

  constructor(private renderer: Renderer2) {
    this.options = this.options || {};
    this.onBeginEvent = new EventEmitter();
    this.onEndEvent = new EventEmitter();
  }

  ngAfterViewInit(): void {
    if (this.options?.canvasHeight) {
      this.renderer.setProperty(
        this.canvasRef.nativeElement,
        'height',
        this.options?.canvasHeight
      );
    }

    if (this.options?.canvasWidth) {
      this.renderer.setProperty(
        this.canvasRef.nativeElement,
        'width',
        this.options?.canvasWidth
      );
    }

    this.signaturePad = new SignaturePad(this.canvasRef.nativeElement,
      {
        ...this.options,
        onBegin: this.onBegin.bind(this),
        onEnd: this.onEnd.bind(this),
      });
  }

  ngOnDestroy(): void {
    this.renderer.setProperty(
      this.canvasRef.nativeElement,
      'height',
      0
    );

    this.renderer.setProperty(
      this.canvasRef.nativeElement,
      'width',
      0
    );
  }

  public resizeCanvas(): void {
    const ratio: number = Math.max(window.devicePixelRatio || 1, 1);

    this.renderer.setProperty(
      this.canvasRef.nativeElement,
      'width',
      this.canvasRef.nativeElement.offsetWidth * ratio
    );

    this.renderer.setProperty(
      this.canvasRef.nativeElement,
      'height',
      this.canvasRef.nativeElement.offsetHeight * ratio
    );

    this.canvasRef.nativeElement.getContext('2d').scale(ratio, ratio);
    this.signaturePad.clear();
  }

  // Returns signature image as an array of point groups
  public toData(): PointGroup[] {
    if (this.signaturePad) {
      return this.signaturePad.toData();
    } else {
      return [];
    }
  }

  // Draws signature image from an array of point groups
  public fromData(points: PointGroup[]): void {
    this.signaturePad.fromData(points);
  }

  public toDataURL(imageType?: string, quality?: number): string {
    return this.signaturePad.toDataURL(imageType, quality);
  }

  public fromDataURL(dataURL: string, options?: {
    ratio?: number;
    width?: number;
    height?: number;
  }): void {
    if (options && !options.hasOwnProperty) {
      options.height = this.options?.canvasHeight;
      options.width = this.options?.canvasWidth;
    }
    this.signaturePad.fromDataURL(dataURL, options);
  }

  // Clears the canvas
  public clear(): void {
    this.signaturePad.clear();
  }

  // Returns true if canvas is empty, otherwise returns false
  public isEmpty(): boolean {
    return this.signaturePad.isEmpty();
  }

  // Unbinds all event handlers
  public off(): void {
    this.signaturePad.off();
  }

  // Rebinds all event handlers
  public on(): void {
    this.signaturePad.on();
  }

  public setOptions(opt: string, value: string | number): void {
    const options = {
      'canvasHeight': () => this.renderer.setProperty(
        this.canvasRef.nativeElement,
        'height',
        value
      ),
      'canvasWidth': () => this.renderer.setProperty(
        this.canvasRef.nativeElement,
        'width',
        value
      ),
    };

    if (!options[opt]) {
      this.signaturePad[opt] = value
      return;
    }

    options[opt]();
  }

  // notify subscribers on signature begin
  public onBegin(): void {
    this.onBeginEvent.emit(true);
  }

  // notify subscribers on signature end
  public onEnd(): void {
    this.onEndEvent.emit(true);
  }

  public queryPad(): SignaturePad {
    return this.signaturePad;
  }
}
